import {
  WSAvatar,
  WSButton,
  WSFlexBox,
  WSMenuItem,
  WSScreenSizesType,
  WSTable,
  WSTableColumnJustify,
  WSTableItem,
  WSTableProps,
  WSText,
  useIsMobile,
  useWSModal
} from "@wingspanhq/fe-component-library";
import {
  IInvoice,
  IInvoiceTemplate,
  InvoiceStatus
} from "@wingspanhq/payments/dist/interfaces";
import React, { useCallback } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useUserId } from "../../../query/hooks/helpers";
import { useFeatureFlags } from "../../../query/hooks/useFeatureFlags";
import { usePayoutSettings } from "../../../query/payments/queries";
import { getIsInstantPayoutAllowed } from "../../../query/payments/selectors";
import { useMemberProfile } from "../../../query/users/queries";
import { openInNewTab } from "../../../utils/openInNewTab";
import { selectorIsInvoiceDeposited } from "../../selectors/selectorIsInvoiceDeposited";
import { calculateLineItemsTotal } from "../../utils";
import { calculateAmountAvailableToRefund } from "../../utils/calculateAmountAvailableToRefund";
import {
  useGetInvoiceActions,
  useGetInvoiceTemplateActions
} from "../InvoiceActions/InvoiceActions";
import { ModalRefund } from "../ModalRefund";
import { ButtonEmailClient } from "./ButtonEmailClient";
import { ButtonRequestInstantPayout } from "./ButtonRequestInstantPayout";
import { ClientDetails } from "./ClientDetails";
import { InvoiceRecentUpdate } from "./InvoiceRecentUpdate";
import { InvoiceTemplateRecentUpdate } from "./InvoiceTemplateRecentUpdate";
import styles from "./InvoicesTable.module.scss";
import { buildInvoiceIcon } from "./buildInvoiceIcon";
import { buildInvoiceTemplateIcon } from "./buildInvoiceTemplateIcon";

export type InvoicesTableItem = WSTableItem<InvoicesTableItemData>;

export type InvoicesTableItemData = {
  updatedAt: Date;
  invoice?: IInvoice;
  invoiceTemplate?: IInvoiceTemplate;
};

type Props = {
  tableData: InvoicesTableItem[];
  listName?: "paid" | "list";
} & WSTableProps<InvoicesTableItemData>;

export const Table: React.FC<Props> = React.memo(
  ({ tableData, listName, ...tableProps }) => {
    const history = useHistory();
    const location = useLocation();
    const isMobile = useIsMobile();
    const userId = useUserId();
    const payoutSettingsQuery = usePayoutSettings(userId);
    const getInvoiceActions = useGetInvoiceActions();
    const getInvoiceTemplateActions = useGetInvoiceTemplateActions();
    const featureFlagsQuery = useFeatureFlags();
    const memberQuery = useMemberProfile(userId);
    const modalRefund = useWSModal(ModalRefund, { size: "S" });

    const onView = useCallback(
      (item: InvoicesTableItem) => {
        if (item.data.invoice) {
          history.push({
            pathname: `/member/invoices${listName ? `/${listName}` : ""}/${
              item.id
            }`,
            search: location.search
          });
        } else if (item.data.invoiceTemplate) {
          history.push({
            pathname: `/member/invoices${
              listName ? `/${listName}` : ""
            }/template/${item.id}`,
            search: location.search
          });
        }
      },
      [history, listName, location.search]
    );

    const onViewInNewTab = useCallback(
      (item: InvoicesTableItem) => {
        const listNameString = listName ? `/${listName}` : "";

        if (item.data.invoice) {
          openInNewTab(
            `/member/invoices${listNameString}/${item.id}${location.search}`
          );
        } else if (item.data.invoiceTemplate) {
          openInNewTab(
            `/member/invoices${listNameString}/template/${item.id}${location.search}`
          );
        }
      },
      [listName, location.search]
    );

    return (
      <WSTable<InvoicesTableItemData>
        {...tableProps}
        tableData={tableData}
        columns={[
          {
            config: {
              gridTemplateSizeMax: "0.7fr"
            },
            renderFunction: ({ data: { invoice, invoiceTemplate } }) => {
              const memberClientId =
                invoice?.memberClientId ||
                invoiceTemplate?.invoiceData.memberClientId;

              if (!memberClientId) return null;

              return (
                <WSFlexBox alignItems="center">
                  {invoice && (
                    <WSAvatar
                      type="icon"
                      {...buildInvoiceIcon(invoice)}
                      mr="M"
                    />
                  )}

                  {invoiceTemplate && (
                    <WSAvatar
                      type="icon"
                      {...buildInvoiceTemplateIcon(invoiceTemplate)}
                      mr="M"
                    />
                  )}
                  <ClientDetails memberClientId={memberClientId as string} />
                </WSFlexBox>
              );
            }
          },
          ...(featureFlagsQuery.data?.customPayablesColumns
            ? [
                {
                  config: {
                    gridTemplateSizeMax: "0.4fr",
                    justify: "center" as WSTableColumnJustify,
                    hideOnScreens: ["XS", "S"] as WSScreenSizesType[]
                  },
                  renderFunction: ({ data: { invoice } }: WSTableItem<any>) =>
                    invoice && (
                      <WSText mb="XS" color="gray600">
                        {invoice.invoiceNumber}
                      </WSText>
                    )
                }
              ]
            : []),
          ...(featureFlagsQuery.data?.customPayablesColumns
            ? [
                {
                  config: {
                    gridTemplateSizeMax: "0.7fr",
                    hideOnScreens: ["XS", "S"] as WSScreenSizesType[]
                  },
                  renderFunction: ({
                    data: { invoice, invoiceTemplate }
                  }: WSTableItem<InvoicesTableItemData>) => {
                    let description = "";
                    if (invoice && invoice.lineItems.length > 0) {
                      description = invoice.lineItems[0].description ?? "";
                    } else if (
                      invoiceTemplate &&
                      invoiceTemplate.invoiceData?.lineItems.length > 0
                    ) {
                      description =
                        invoiceTemplate.invoiceData?.lineItems[0].description ??
                        "";
                    }
                    return (
                      <WSFlexBox.CenterY alignItems="flex-end">
                        <WSText
                          p="XS"
                          color="gray600"
                          title={description}
                          className={styles.twoLineEllipsis}
                        >
                          {description}
                        </WSText>
                      </WSFlexBox.CenterY>
                    );
                  }
                }
              ]
            : []),
          {
            config: {
              gridTemplateSizeMax: "0.6fr",
              justify: "end",
              hideOnScreens: ["XS", "S"]
            },
            renderFunction: ({ data: { invoice } }) =>
              invoice ? (
                invoice.status === InvoiceStatus.Overdue ? (
                  <ButtonEmailClient invoice={invoice} />
                ) : invoice.status === InvoiceStatus.PaymentInTransit ? (
                  <ButtonEmailClient invoice={invoice} />
                ) : payoutSettingsQuery.data &&
                  memberQuery.data &&
                  getIsInstantPayoutAllowed(
                    invoice,
                    payoutSettingsQuery.data,
                    memberQuery.data
                  ) ? (
                  <ButtonRequestInstantPayout invoiceId={invoice.invoiceId} />
                ) : featureFlagsQuery.data?.refundButton &&
                  selectorIsInvoiceDeposited(invoice) &&
                  calculateAmountAvailableToRefund(invoice) > 0 ? (
                  <WSButton.Link
                    onClick={() => {
                      modalRefund.open({ invoice });
                    }}
                  >
                    Refund
                  </WSButton.Link>
                ) : null
              ) : null
          },
          {
            config: {
              gridTemplateSizeMax: "0.6fr",
              justify: "end"
            },
            renderFunction: ({ data: { invoice, invoiceTemplate } }) => (
              <WSFlexBox direction="column" alignItems="flex-end">
                <WSText
                  mb={isMobile ? undefined : "XS"}
                  formatMoney
                  color="gray600"
                >
                  {invoice
                    ? calculateLineItemsTotal(invoice.lineItems)
                    : invoiceTemplate
                    ? calculateLineItemsTotal(
                        invoiceTemplate.invoiceData.lineItems
                      )
                    : null}
                </WSText>
                {invoice ? (
                  <InvoiceRecentUpdate invoice={invoice} />
                ) : invoiceTemplate ? (
                  <InvoiceTemplateRecentUpdate
                    invoiceTemplate={invoiceTemplate}
                  />
                ) : null}
              </WSFlexBox>
            )
          }
        ]}
        onRowClick={onView}
        onRowControlClick={onViewInNewTab}
        onRowMousewheelClick={onViewInNewTab}
        rowMenuActions={item => {
          const {
            data: { invoice, invoiceTemplate }
          } = item;
          const menuItems: WSMenuItem[] = [];

          menuItems.push({
            icon: "search",
            label: "View",
            onClick: () => {
              onView(item);
            }
          });

          if (invoice) {
            menuItems.push(...getInvoiceActions(invoice));
          } else if (invoiceTemplate) {
            menuItems.push(...getInvoiceTemplateActions(invoiceTemplate));
          }

          return menuItems;
        }}
      />
    );
  },
  (prevProps, nextProps) => {
    if (prevProps.tableData.length !== nextProps.tableData.length) {
      return false;
    }

    for (let index = 0; index < (prevProps.tableData?.length || 0); index++) {
      if (
        prevProps.tableData[index].data.updatedAt.getTime() !==
        nextProps.tableData[index].data.updatedAt.getTime()
      )
        return false;
    }

    return true;
  }
);
